/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  Save,
  Building2,
  Globe,
  Layers,
  Plug,
  Bot,
  KeyRound,
  Hash,
} from 'lucide-react';
import { accountService } from '../services/accountService';
import { serviceRequestTypeService } from '../services/serviceRequestTypeService';
import MultiSelect from '../components/MultiSelect';
import BrandingSection from '../components/BrandingSection';
import PixoWebhookSettings from '../components/accounts/PixoWebhookSettings';
import { subAccountService } from '../services/subAccountService';
import Modal from '../components/Modal';
import toastService from '../utils/toastService';
import { useAuth } from '../context/AuthContext';
import { PIXO_ENVIRONMENT_OPTIONS } from '../constants/pixo';
import { AI_AGENT_HOST_MAX_LENGTH, validateAiAgentHost } from '../constants/aiAgents';
import { ACCOUNT_LABEL, SUB_ACCOUNT_LABEL, SERVICE_REQUEST_LABEL } from '../constants/entityLabels';

const ACCOUNTS_ROUTE = '/accounts';

const EditAccount = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, refreshBranding } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [hasSubAccounts, setHasSubAccounts] = useState(false);
  const [form, setForm] = useState({
    name: '',
    slug: '',
    status: 'active',
    truvideoAccountUID: '',
    externalAccountId: '',
    pixoEnabled: false,
    pixoEnvironment: '',
    pixoModuleId: '',
    aiAgentEnabled: false,
    aiAgentTenantId: '',
    aiAgentHost: '',
    serviceRequestTypeIds: [],
    // Account branding — empty fields use the platform default
    theme: '',
    logo_url: '',
    favicon_url: '',
  });
  const [fieldErrors, setFieldErrors] = useState({});
  // As saved on the server — the Pixo webhook URL and settings depend on these, not on unsaved edits
  const [savedAccount, setSavedAccount] = useState({ slug: '', pixoEnabled: false });

  const handleLogoUpload = useCallback(async (file) => {
    const res = await subAccountService.uploadLogo(file);
    return res.data; // { url: string }
  }, []);

  const handleFaviconUpload = useCallback(async (file) => {
    const res = await subAccountService.uploadLogo(file, null, 'favicon');
    return res.data; // { url: string }
  }, []);
  const [serviceRequestTypes, setServiceRequestTypes] = useState([]);
  const [pendingTypeRemoval, setPendingTypeRemoval] = useState(null);

  useEffect(() => {
    const loadTypes = async () => {
      try {
        const { data } = await serviceRequestTypeService.getServiceRequestTypes({ limit: 100 });
        setServiceRequestTypes(data.data || []);
      } catch {
        // Non-fatal — the multi-select just shows no options
      }
    };
    loadTypes();
  }, []);

  useEffect(() => {
    const loadAccount = async () => {
      try {
        setLoading(true);
        const { data: account } = await accountService.getAccountById(id);

        setHasSubAccounts(parseInt(account.subAccountCount, 10) > 0);
        setSavedAccount({
          slug: account.slug || '',
          pixoEnabled: account.metadata?.pixo?.enabled === true,
        });
        setForm({
          name: account.name || '',
          slug: account.slug || '',
          status: account.status || 'active',
          truvideoAccountUID: account.truvideoAccountUID || '',
          externalAccountId: account.externalAccountId || '',
          pixoEnabled: account.metadata?.pixo?.enabled || false,
          pixoEnvironment: account.metadata?.pixo?.environment || '',
          pixoModuleId: account.metadata?.pixo?.moduleId || '',
          aiAgentEnabled: account.metadata?.aiAgent?.enabled || false,
          aiAgentTenantId: account.metadata?.aiAgent?.tenant_id || '',
          aiAgentHost: account.metadata?.aiAgent?.host || '',
          serviceRequestTypeIds: account.serviceRequestTypeIds || [],
          theme: account.theme || '',
          logo_url: account.logoUrl || '',
          favicon_url: account.faviconUrl || '',
        });
      } catch {
        toastService.error(`Failed to load ${ACCOUNT_LABEL.singularLower} details`);
        navigate(ACCOUNTS_ROUTE);
      } finally {
        setLoading(false);
      }
    };
    loadAccount();
  }, [id, navigate]);

  const handleServiceRequestTypesChange = (newIds) => {
    const removedId = form.serviceRequestTypeIds.find((typeId) => !newIds.includes(typeId));
    if (removedId) {
      const removedType = serviceRequestTypes.find((t) => t.uid === removedId);
      setPendingTypeRemoval({ newIds, removedType });
      return;
    }
    setForm((f) => ({ ...f, serviceRequestTypeIds: newIds }));
  };

  const validateForm = () => {
    const errors = {};
    if (!form.name.trim()) {
      errors.name = `${ACCOUNT_LABEL.singular} name is required`;
    }
    if (form.pixoEnabled) {
      if (!form.pixoEnvironment) {
        errors.pixoEnvironment = 'Environment is required when Pixo Integration is enabled';
      }
      if (!form.pixoModuleId.trim()) {
        errors.pixoModuleId = 'Module ID is required when Pixo Integration is enabled';
      }
    }
    // The host is locked (read-only) once a tenant exists, so only validate while editable.
    const aiAgentHostError = form.aiAgentTenantId ? null : validateAiAgentHost(form.aiAgentHost);
    if (aiAgentHostError) {
      errors.aiAgentHost = aiAgentHostError;
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBlur = (field, value) => {
    if (field === 'name' && !value.trim()) {
      setFieldErrors((prev) => ({ ...prev, name: `${ACCOUNT_LABEL.singular} name is required` }));
    } else {
      setFieldErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    try {
      setSubmitting(true);
      await accountService.updateAccount(id, {
        name: form.name.trim(),
        status: form.status,
        externalAccountId: form.externalAccountId.trim(),
        serviceRequestTypeIds: form.serviceRequestTypeIds,
        theme: form.theme,
        logoUrl: form.logo_url,
        faviconUrl: form.favicon_url,
        metadata: {
          pixo: {
            enabled: form.pixoEnabled,
            ...(form.pixoEnvironment ? { environment: form.pixoEnvironment } : {}),
            ...(form.pixoModuleId.trim() ? { moduleId: form.pixoModuleId.trim() } : {}),
          },
          // tenant_id is server-managed — never sent from the client. An empty
          // host means AGENTICAI_HOST; the server ignores it once a tenant exists.
          aiAgent: {
            enabled: form.aiAgentEnabled,
            host: form.aiAgentHost.trim(),
          },
        },
      });
      if (user?.accountUID === id && !user?.subAccountUID) {
        await refreshBranding().catch(() => {});
      }
      toastService.success(`${ACCOUNT_LABEL.singular} updated successfully`);
      navigate(ACCOUNTS_ROUTE);
    } catch (err) {
      const msg = err.response?.data?.message;
      const errorMsg = Array.isArray(msg) ? msg[0] : (msg || `Failed to update ${ACCOUNT_LABEL.singularLower}`);
      toastService.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content placeholder:text-base-content/30 focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all ${
      fieldErrors[field] ? 'border-error' : 'border-base-300'
    }`;

  const selectClass = (field) =>
    `w-full px-4 py-2.5 bg-base-100 border rounded-xl text-sm text-base-content focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all font-medium ${
      fieldErrors[field] ? 'border-error' : 'border-base-300'
    }`;

  const selectedPixoEnvironment = PIXO_ENVIRONMENT_OPTIONS.find((o) => o.value === form.pixoEnvironment);

  if (loading) {
    return (
      <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
        <div className="h-4 w-32 bg-base-300/60 rounded animate-pulse"></div>
        <div className="glass-card rounded-2xl p-6 border border-base-content/10">
          <div className="h-7 w-48 bg-base-300/60 rounded mb-2 animate-pulse"></div>
          <div className="h-4 w-80 bg-base-200 rounded animate-pulse"></div>
        </div>
        <div className="glass-card rounded-2xl p-6 border border-base-content/10 space-y-4">
          <div className="h-5 w-40 bg-base-300/60 rounded animate-pulse"></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="h-10 bg-base-200/60 rounded-xl animate-pulse"></div>
            <div className="h-10 bg-base-200/60 rounded-xl animate-pulse"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-1 sm:p-2 w-full pb-12">
      {/* Top Breadcrumb */}
      <div>
        <button
          type="button"
          onClick={() => navigate(ACCOUNTS_ROUTE)}
          className="inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-base-content/50 hover:text-primary transition-colors group"
        >
          <ArrowLeft size={14} className="group-hover:-translate-x-1 transition-transform" />
          Back to {ACCOUNT_LABEL.plural}
        </button>
      </div>

      {/* Hero Header Banner */}
      <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-base-content/40">
                Edit Record
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black text-base-content tracking-tight">
              Update {ACCOUNT_LABEL.singular}
            </h1>
            <p className="text-xs text-base-content/50">
              Modify {ACCOUNT_LABEL.singularLower} details and associated {SERVICE_REQUEST_LABEL.singularLower} types.
            </p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Account Information */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center gap-2 pb-3 border-b border-base-content/10">
            <Building2 size={18} className="text-primary" />
            <h2 className="text-base font-black text-base-content uppercase tracking-wider">
              {ACCOUNT_LABEL.singular} Information
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Account Name */}
            <div className="space-y-1.5">
              <label
                htmlFor="name"
                className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5"
              >
                <Building2 size={12} className="text-primary" />
                <span>{ACCOUNT_LABEL.singular} Name *</span>
              </label>
              <input
                id="name"
                type="text"
                maxLength={50}
                placeholder="e.g. Acme Corporation"
                className={inputClass('name')}
                value={form.name}
                onBlur={(e) => handleBlur('name', e.target.value)}
                onChange={(e) => {
                  setForm((f) => ({ ...f, name: e.target.value }));
                  if (fieldErrors.name) setFieldErrors((prev) => ({ ...prev, name: null }));
                }}
              />
              {fieldErrors.name && (
                <p className="text-error text-xs font-medium">{fieldErrors.name}</p>
              )}
            </div>

            {/* Slug — Read-only */}
            <div className="space-y-1.5">
              <label
                htmlFor="slug"
                className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5"
              >
                <Globe size={12} className="text-primary" />
                <span>Slug</span>
              </label>
              <div className="relative">
                <Globe size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-base-content/30" />
                <input
                  id="slug"
                  type="text"
                  readOnly
                  tabIndex={-1}
                  className="w-full pl-10 pr-4 py-2.5 bg-base-200/60 border border-base-300 rounded-xl text-sm text-base-content/60 font-mono outline-none cursor-not-allowed"
                  value={form.slug}
                />
              </div>
              <p className="text-base-content/40 text-[11px] font-medium">
                Slug cannot be changed after {ACCOUNT_LABEL.singularLower} creation.
              </p>
            </div>

            {/* External Account ID */}
            <div className="space-y-1.5">
              <label
                htmlFor="externalAccountId"
                className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5"
              >
                <Hash size={12} className="text-primary" />
                <span>External {ACCOUNT_LABEL.singular} ID</span>
              </label>
              <input
                id="externalAccountId"
                type="text"
                maxLength={255}
                placeholder="Optional"
                className={inputClass('externalAccountId')}
                value={form.externalAccountId}
                onChange={(e) => setForm((f) => ({ ...f, externalAccountId: e.target.value }))}
              />
            </div>
          </div>
        </div>

        {/* Pixo Integration */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-base-content/10">
            <div className="flex items-center gap-2">
              <Plug size={18} className="text-primary" />
              <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                Pixo Integration
              </h2>
            </div>
            <span className="text-[10px] text-base-content/40 font-mono">Optional</span>
          </div>

          {/* Enable Toggle */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-base-200/50 border border-base-content/10">
            <div>
              <p className="text-xs font-black text-base-content tracking-tight">Enable Pixo Integration</p>
              <p className="text-[11px] text-base-content/40 mt-0.5">With the Pixo integration enabled, the Pixo platform will serve as the authorization provider for login requests.</p>
            </div>
            <label aria-label="Enable Pixo Integration toggle" className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={form.pixoEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setForm((f) => ({ ...f, pixoEnabled: enabled }));
                  if (!enabled) {
                    setFieldErrors((prev) => ({ ...prev, pixoEnvironment: null, pixoModuleId: null }));
                  }
                }}
              />
              <div className="w-11 h-6 bg-base-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary shadow-inner"></div>
            </label>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Environment */}
            <div className="space-y-1.5">
              <label
                htmlFor="pixoEnvironment"
                className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5"
              >
                <Globe size={12} className="text-primary" />
                <span>Environment</span>
              </label>
              <select
                id="pixoEnvironment"
                className={selectClass('pixoEnvironment')}
                value={form.pixoEnvironment}
                onChange={(e) => {
                  const value = e.target.value;
                  setForm((f) => ({ ...f, pixoEnvironment: value }));
                  if (fieldErrors.pixoEnvironment) setFieldErrors((prev) => ({ ...prev, pixoEnvironment: null }));
                }}
              >
                <option value="">Select environment</option>
                {PIXO_ENVIRONMENT_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              {selectedPixoEnvironment && (
                <>
                  <p className="text-base-content/40 text-[11px] font-mono truncate">
                    Platform: {selectedPixoEnvironment.platformHost}
                  </p>
                  <p className="text-base-content/40 text-[11px] font-mono truncate">
                    Modules: {selectedPixoEnvironment.host}
                  </p>
                </>
              )}
              {fieldErrors.pixoEnvironment && (
                <p className="text-error text-xs font-medium">{fieldErrors.pixoEnvironment}</p>
              )}
            </div>

            {/* Module ID */}
            <div className="space-y-1.5">
              <label
                htmlFor="pixoModuleId"
                className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5"
              >
                <Plug size={12} className="text-primary" />
                <span>Module ID</span>
              </label>
              <input
                id="pixoModuleId"
                type="text"
                maxLength={100}
                placeholder="e.g. 123456"
                className={inputClass('pixoModuleId')}
                value={form.pixoModuleId}
                onChange={(e) => {
                  const value = e.target.value;
                  setForm((f) => ({ ...f, pixoModuleId: value }));
                  if (fieldErrors.pixoModuleId) setFieldErrors((prev) => ({ ...prev, pixoModuleId: null }));
                }}
              />
              {fieldErrors.pixoModuleId && (
                <p className="text-error text-xs font-medium">{fieldErrors.pixoModuleId}</p>
              )}
            </div>
          </div>

          {/* Pixo user-sync webhook — shown once Pixo is enabled in the form */}
          {form.pixoEnabled && savedAccount.slug && (
            <PixoWebhookSettings
              accountId={id}
              accountSlug={savedAccount.slug}
              pixoEnabled={savedAccount.pixoEnabled}
            />
          )}
        </div>

        {/* AI Agent Integration */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4">
          <div className="flex items-center justify-between pb-3 border-b border-base-content/10">
            <div className="flex items-center gap-2">
              <Bot size={18} className="text-primary" />
              <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                AI Agent Integration
              </h2>
            </div>
            <span className="text-[10px] text-base-content/40 font-mono">Optional</span>
          </div>

          {/* Enable Toggle */}
          <div className="flex items-center justify-between p-4 rounded-xl bg-base-200/50 border border-base-content/10">
            <div>
              <p className="text-xs font-black text-base-content tracking-tight">Enable AI Agent Integration</p>
              <p className="text-[11px] text-base-content/40 mt-0.5">When enabled, an AI Agent tenant is provisioned for this {ACCOUNT_LABEL.singularLower}.</p>
            </div>
            <label aria-label="Enable AI Agent Integration toggle" className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                className="sr-only peer"
                checked={form.aiAgentEnabled}
                onChange={(e) => {
                  const enabled = e.target.checked;
                  setForm((f) => ({ ...f, aiAgentEnabled: enabled }));
                }}
              />
              <div className="w-11 h-6 bg-base-300 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary shadow-inner"></div>
            </label>
          </div>

          {/* AgenticAI Host — optional (falls back to the server default); locked once a tenant exists */}
          <div className="space-y-1.5">
            <label
              htmlFor="aiAgentHost"
              className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5"
            >
              <Globe size={12} className="text-primary" />
              <span>AgenticAI Host</span>
            </label>
            {form.aiAgentTenantId ? (
              <input
                id="aiAgentHost"
                type="text"
                readOnly
                tabIndex={-1}
                placeholder="Server default"
                className="w-full px-4 py-2.5 bg-base-200/60 border border-base-300 rounded-xl text-sm text-base-content/60 font-mono outline-none cursor-not-allowed placeholder:text-base-content/40"
                value={form.aiAgentHost}
              />
            ) : (
              <input
                id="aiAgentHost"
                type="text"
                maxLength={AI_AGENT_HOST_MAX_LENGTH}
                placeholder="e.g. https://agentic-ai.example.com"
                className={inputClass('aiAgentHost')}
                value={form.aiAgentHost}
                onChange={(e) => {
                  const value = e.target.value;
                  setForm((f) => ({ ...f, aiAgentHost: value }));
                  if (fieldErrors.aiAgentHost) setFieldErrors((prev) => ({ ...prev, aiAgentHost: null }));
                }}
              />
            )}
            {fieldErrors.aiAgentHost ? (
              <p className="text-error text-xs font-medium">{fieldErrors.aiAgentHost}</p>
            ) : (
              <p className="text-base-content/40 text-[11px] font-medium">
                {form.aiAgentTenantId
                  ? 'Locked after the tenant is provisioned.'
                  : 'Optional. Leave empty to use the server default. Cannot be changed once the tenant is provisioned.'}
              </p>
            )}
          </div>

          {/* Tenant ID — Read-only, provisioned by the server */}
          {form.aiAgentTenantId && (
            <div className="space-y-1.5">
              <label
                htmlFor="aiAgentTenantId"
                className="text-[10px] font-black text-base-content/50 uppercase tracking-widest flex items-center gap-1.5"
              >
                <KeyRound size={12} className="text-primary" />
                <span>Tenant ID</span>
              </label>
              <input
                id="aiAgentTenantId"
                type="text"
                readOnly
                tabIndex={-1}
                className="w-full px-4 py-2.5 bg-base-200/60 border border-base-300 rounded-xl text-sm text-base-content/60 font-mono outline-none cursor-not-allowed"
                value={form.aiAgentTenantId}
              />
              <p className="text-base-content/40 text-[11px] font-medium">
                Provisioned automatically; cannot be changed.
              </p>
            </div>
          )}
        </div>

        {/* Service Request Types */}
        <div className="glass-card rounded-2xl p-5 sm:p-6 shadow-sm border border-base-content/10 space-y-4 relative z-20">
          <div className="flex items-center justify-between pb-3 border-b border-base-content/10">
            <div className="flex items-center gap-2">
              <Layers size={18} className="text-primary" />
              <h2 className="text-base font-black text-base-content uppercase tracking-wider">
                {SERVICE_REQUEST_LABEL.singular} Types
              </h2>
            </div>
            <span className="text-[10px] text-base-content/40 font-mono">Optional</span>
          </div>

          <div className="space-y-1.5">
            <MultiSelect
              label={`Assigned ${SERVICE_REQUEST_LABEL.singular} Types`}
              searchPlaceholder={`Search ${SERVICE_REQUEST_LABEL.singularLower} types...`}
              placeholder={`Select ${SERVICE_REQUEST_LABEL.singularLower} types (optional)`}
              options={serviceRequestTypes.map((t) => ({ id: t.uid, key: t.name }))}
              selected={form.serviceRequestTypeIds}
              onChange={handleServiceRequestTypesChange}
            />
          </div>
        </div>

        {/* Account Branding — sub-accounts without their own branding use these */}
        <BrandingSection
          title={`${ACCOUNT_LABEL.singular} Branding`}
          value={form}
          onChange={(patch) => setForm(f => ({ ...f, ...patch }))}
          uploadLogo={handleLogoUpload}
          uploadFavicon={handleFaviconUpload}
          radioName="account-theme"
        />

        {/* Form Actions */}
        <div className="flex flex-col-reverse sm:flex-row items-center gap-3 pt-2 relative z-0">
          <button
            type="button"
            onClick={() => navigate(ACCOUNTS_ROUTE)}
            className="w-full sm:w-auto px-6 py-3 border border-base-content/20 text-base-content/70 hover:bg-base-200 rounded-xl font-bold text-sm transition-all"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="w-full sm:flex-1 btn-premium justify-center"
          >
            {submitting ? (
              <>
                <div className="w-4 h-4 border-2 border-primary-content/30 border-t-primary-content rounded-full animate-spin"></div>
                <span>Saving Changes...</span>
              </>
            ) : (
              <>
                <Save size={16} />
                <span>Save Changes</span>
              </>
            )}
          </button>
        </div>
      </form>

      {/* Remove Type Confirmation Modal */}
      <Modal
        isOpen={!!pendingTypeRemoval}
        onClose={() => setPendingTypeRemoval(null)}
        title={`Remove ${SERVICE_REQUEST_LABEL.singular} Type`}
      >
        <div className="mb-6 space-y-3">
          {hasSubAccounts ? (
            <>
              <p className="text-base-content/70 text-sm leading-relaxed">
                Removing{' '}
                <strong className="text-base-content font-semibold">
                  {pendingTypeRemoval?.removedType?.name}
                </strong>{' '}
                will also remove it from any {SUB_ACCOUNT_LABEL.pluralLower} under this {ACCOUNT_LABEL.singularLower} that currently have it assigned.
              </p>
              <div className="p-3 rounded-xl bg-warning/10 border border-warning/20 text-warning text-xs font-semibold">
                {SUB_ACCOUNT_LABEL.plural} relying on this {SERVICE_REQUEST_LABEL.singularLower} type will no longer be able to create {SERVICE_REQUEST_LABEL.pluralLower} for it.
              </div>
            </>
          ) : (
            <p className="text-base-content/70 text-sm leading-relaxed">
              Are you sure you want to remove{' '}
              <strong className="text-base-content font-semibold">
                {pendingTypeRemoval?.removedType?.name}
              </strong>{' '}
              from this {ACCOUNT_LABEL.singularLower}?
            </p>
          )}
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => setPendingTypeRemoval(null)}
            className="flex-1 px-4 py-2.5 border border-base-content/20 text-base-content/70 rounded-xl font-bold text-sm hover:bg-base-200 transition"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => {
              setForm((f) => ({ ...f, serviceRequestTypeIds: pendingTypeRemoval.newIds }));
              setPendingTypeRemoval(null);
            }}
            className="flex-1 px-4 py-2.5 bg-error text-error-content rounded-xl font-bold text-sm hover:opacity-90 transition"
          >
            Remove
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default EditAccount;
